import {
  Component, OnInit,
  ViewChild, ElementRef,
  AfterViewInit, OnDestroy, ComponentRef,
} from '@angular/core';
import { SynchronizationService } from '../../../services/synchronization.service';
import { SettingsService } from '../../../services/settings.service';
import { Subscription } from 'rxjs';
import { ConfigLoaderService } from '../../../config/configloader.service';
import { InteractionService } from '../../../services/interaction.service';
import { SprintService } from '../../../services/sprint.service';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { IWidget } from '../../../interfaces/iWidget';

@Component({
  selector: 'app-app-widget',
  templateUrl: './app-widget.component.html',
  styleUrls: ['./app-widget.component.scss']
  })
export class AppWidgetComponent implements IWidget, OnInit, AfterViewInit, OnDestroy {
  static widgetName = 'app';
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  private interactedClass = 'resizedwidget';
  isSmallView: boolean;
  private event: Subscription[] = [];
  qrData: string;
  projects: string[];
  public projectId;
  public project: string;
  public sprintId: number;
  public sprintName: string;
  private pyWall: string;
  private syncServer: string;

  // style
  public zIndex = 25;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(
    private settingsService: SettingsService,
    private sprintService: SprintService,
    private configService: ConfigLoaderService,
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper) {
  }

  ngOnInit(): void {
    this.isSmallView = true;
    this.event.push(this.settingsService.getUpdateObservable().subscribe(res => this.loadProject()));
    this.loadProject();
    this.setupRealTimeEvents();
  }

  ngAfterViewInit() {
    this.setupinteraction();
  }

  ngOnDestroy() {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    // Cleanup interactjs-listeners
    this.interactionService.unset('.appwidget');
  }

  /**
   * Get the parameters of the actual project and generate the Code.
   */
  loadProject() {
    this.project = this.settingsService.getProjectName();
    this.pyWall = this.configService.settings.pyWallServer.url;
    this.syncServer = this.configService.settings.pySyncServer.url + this.configService.settings.pySyncServer.options.path;
    this.sprintId = this.settingsService.getSprintId(this.project);
    this.sprintService.getSprintDetails(this.project, this.sprintId).subscribe(sprint => {
      this.sprintName = sprint.name;
      this.generateCode(this.project, this.pyWall, this.syncServer, this.sprintId.toString(), this.sprintName);
    });
  }

  /**
   * Generate the QR Code for the Ionic-Application to connect to the active project.
   */
  generateCode(project: string, pyWall: string, syncServer: string, sprintId: string, sprintName: string) {
    this.qrData = project + ';' + pyWall + ';' + syncServer + ';' + sprintId + ';' + sprintName;
  }

  /**
   * Set up the interactions on the widget
   */
  private setupinteraction() {
    this.interactionService.addDragInteraction('.appwidget', (event) => {
      event.target.classList.add(this.interactedClass);
      this.synchronization.sendRealTimeEvent(AppWidgetComponent.widgetName + 'StartMoveWidget');
    }, (positionAbsolute) => {
      const relativePosition = this.positionHelper.getRelativePosition(positionAbsolute.x, positionAbsolute.y);
      this.synchronization.sendRealTimeEvent(AppWidgetComponent.widgetName + 'MoveWidget', relativePosition);
    }, (event) => {
      if (this.isSmallView) {
        const target = this.element.nativeElement;
        target.style.width = '67vh';
        target.style.height = '50vh';
      }
    });
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealTimeEvents() {
    const obj = this;
    this.event.push(this.synchronization.subscribeEvent<any>(AppWidgetComponent.widgetName + 'ResizeWidget')
      .subscribe(size => {
        const target = obj.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(AppWidgetComponent.widgetName + 'StartMoveWidget')
      .subscribe(() => {
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(AppWidgetComponent.widgetName + 'MoveWidget')
      .subscribe(positionRelative => {
        const target = this.element.nativeElement;
        this.positionHelper.setRelativePosition(target, positionRelative.left, positionRelative.top);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(AppWidgetComponent.widgetName + 'ResetWidget')
      .subscribe(() => {
        obj.resetWidget(obj.element.nativeElement);
      }));

    this.event.push(this.synchronization.subscribeEvent<any>(AppWidgetComponent.widgetName + 'SwitchToOrderedView')
      .subscribe(size => {
        const target = this.element.nativeElement;
        if (this.isSmallView) {
          target.style.width = `${size.width}`;
          target.style.height = `${size.height}`;
        }
      }));
  }

  /**
   * Reset callback which can be assigned to element listeners. Resets the interactions on the event target.
   * @param event Fired event from the target element.
   */
  reset(event: any) {
    this.synchronization.sendRealTimeEvent(AppWidgetComponent.widgetName + 'ResetWidget');
    const closest = event.target.closest(`.${this.interactedClass}`);
    this.resetWidget(closest);
  }

  /**
   * Resets the interactions on the widget.
   * @param target The widget target as HTMLElement which should be reset.
   */
  resetWidget(target: HTMLElement) {
    this.interactionService.undoDragInteraction(target);
    this.interactionService.undoPinchToResizeInteraction(target);
    target.classList.remove(this.interactedClass);
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
